/**
 * MCP Integration Settings Page
 *
 * OAuth integration for MCP clients via the Model Context Protocol (MCP).
 * Works with Claude.ai, Claude Desktop, OpenAI Codex, and other MCP-compatible clients.
 * Rendered inside McpSection, which gates the whole section on the `mcp_oauth`
 * feature flag.
 */

import { Check, Copy01, LinkExternal01, Trash01 } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { Key } from 'react-aria-components';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Tab, TabList, TabPanel, Tabs } from '@/lib/base/tabs';
import { getApiBaseUrl } from '@/lib/config';

import { useOrg } from '../../../contexts/OrgContext';
import {
  type McpTool,
  type McpToolSchemaProperty,
  useMcpTools,
} from '../../../hooks/settings/useMcpTools';
import { useOAuthTokens } from '../../../hooks/settings/useOAuthTokens';
import { useTitle } from '../../../hooks/useTitle';

const DEFAULT_CLIENT_TAB = 'claude-ai';

interface McpToolParameter {
  name: string;
  type: string;
  required: boolean;
}

function formatEnum(values: (string | number)[]): string {
  return values
    .map(value => (typeof value === 'string' ? `'${value}'` : String(value)))
    .join(' | ');
}

/**
 * Render a JSON-schema property as a compact human-readable type label.
 *
 * Pydantic-generated schemas express optionality as `anyOf` with a `null`
 * variant and no top-level `type`; a naive `prop.type` read would show
 * those as `any`. Resolve the real type instead: strip the `null` variant
 * (the required badge already conveys optionality), name arrays with their
 * element type (`array<string>`), inline enums as their literal values
 * (`'count' | 'sum'`), and resolve `$ref` pointers via `$defs` — enum
 * refs inline their values, model refs keep the model name (which the
 * glossary below the parameter list then defines). `any` remains only
 * for genuinely unconstrained fields.
 */
function formatSchemaType(
  prop: McpToolSchemaProperty,
  defs: Record<string, McpToolSchemaProperty>
): string {
  if (prop.$ref !== undefined) {
    const refName = prop.$ref.split('/').pop() ?? 'object';
    const target = defs[refName];
    if (target?.enum !== undefined) {
      return formatEnum(target.enum);
    }
    return refName;
  }
  if (prop.enum !== undefined) {
    return formatEnum(prop.enum);
  }
  if (prop.anyOf !== undefined) {
    const variants = [
      ...new Set(
        prop.anyOf
          .map(variant => formatSchemaType(variant, defs))
          .filter(label => label !== 'null')
      ),
    ];
    return variants.length > 0 ? variants.join(' | ') : 'any';
  }
  if (prop.type === 'array') {
    return prop.items
      ? `array<${formatSchemaType(prop.items, defs)}>`
      : 'array';
  }
  return prop.type ?? 'any';
}

function getSchemaParameters(
  properties: Record<string, McpToolSchemaProperty>,
  requiredNames: string[],
  defs: Record<string, McpToolSchemaProperty>
): McpToolParameter[] {
  const required = new Set(requiredNames);
  return Object.entries(properties).map(([name, prop]) => ({
    name,
    type: formatSchemaType(prop, defs),
    required: required.has(name),
  }));
}

/**
 * Object models from the schema's `$defs`, for the per-tool glossary.
 * Enum defs are excluded — their values are inlined into type labels by
 * `formatSchemaType`, so only named models need defining.
 */
function getObjectDefs(
  defs: Record<string, McpToolSchemaProperty>
): [string, McpToolSchemaProperty][] {
  return Object.entries(defs).filter(([, def]) => def.enum === undefined);
}

/**
 * A single settings block: consistent title + description + content, with
 * vertical padding so a `divide-y` parent draws clean separators between
 * blocks. Matches the typographic scale used across the other settings
 * sections (section title `text-lg`, block title `text-base`).
 */
function SettingsBlock({
  title,
  description,
  children,
}: {
  title: string;
  description: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className='space-y-4 py-8 first:pt-0 last:pb-0'>
      <div>
        <h3 className='text-base font-semibold text-fg-primary'>{title}</h3>
        <p className='text-sm text-fg-tertiary mt-0.5'>{description}</p>
      </div>
      {children}
    </section>
  );
}

/** Ordered, numbered steps used inside the "How to Connect" tabs. */
function StepList({ children }: { children: ReactNode }) {
  return (
    <ol className='list-decimal space-y-1.5 pl-5 text-sm text-fg-secondary marker:font-medium marker:text-fg-quaternary'>
      {children}
    </ol>
  );
}

/** A copy-to-clipboard code/command block for terminal + config snippets. */
function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy code to clipboard:', err);
      });
  }, [code]);
  return (
    <div className='relative'>
      <pre className='overflow-x-auto whitespace-pre rounded-lg border border-border-secondary bg-bg-primary p-3 pr-11 font-mono text-xs text-fg-primary'>
        {code}
      </pre>
      <Button
        onClick={handleCopy}
        color='tertiary'
        size='sm'
        iconLeading={copied ? Check : Copy01}
        aria-label='Copy code'
        className='absolute right-1.5 top-1.5'
      />
    </div>
  );
}

/** A bordered instruction card, optionally titled, inside a client tab. */
function InstructionCard({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className='rounded-lg border border-border-secondary bg-bg-secondary p-4'>
      {title && (
        <h4 className='text-sm font-semibold text-fg-primary mb-2'>{title}</h4>
      )}
      {children}
    </div>
  );
}

function ParameterRow({ name, type, required }: McpToolParameter) {
  return (
    <div className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
      <span className='font-mono text-xs text-fg-secondary'>{name}</span>
      <span className='font-mono text-xs text-fg-tertiary'>{type}</span>
      {required && (
        <Badge type='color' size='sm' color='gray'>
          required
        </Badge>
      )}
    </div>
  );
}

function McpToolCard({ tool }: { tool: McpTool }) {
  const defs = tool.input_schema.$defs ?? {};
  const parameters = getSchemaParameters(
    tool.input_schema.properties ?? {},
    tool.input_schema.required ?? [],
    defs
  );
  const objectDefs = getObjectDefs(defs);
  return (
    <AccordionItem className='border border-border-secondary rounded-lg overflow-hidden'>
      <AccordionTrigger className='w-full px-4 py-3 text-left'>
        <div className='flex items-center justify-between gap-2'>
          <p className='font-mono text-sm font-semibold text-fg-primary'>
            {tool.name}
          </p>
          <AccordionChevron variant='swap' />
        </div>
      </AccordionTrigger>
      <AccordionPanel className='px-4 pb-4 space-y-2'>
        <p className='text-sm text-fg-tertiary'>{tool.description}</p>
        {parameters.length > 0 && (
          <div className='space-y-1 pt-1'>
            {parameters.map(param => (
              <ParameterRow key={param.name} {...param} />
            ))}
          </div>
        )}
        {objectDefs.length > 0 && (
          <div className='space-y-2 pt-2'>
            {objectDefs.map(([defName, def]) => (
              <div key={defName}>
                <p className='font-mono text-xs font-semibold text-fg-secondary mb-1'>
                  {defName}
                </p>
                <div className='pl-3 border-l border-border-secondary space-y-1'>
                  {getSchemaParameters(
                    def.properties ?? {},
                    def.required ?? [],
                    defs
                  ).map(field => (
                    <ParameterRow key={field.name} {...field} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
}

function McpToolsSection({ orgId }: { orgId: number }) {
  const { data: tools, isLoading, error } = useMcpTools(orgId);

  let content: ReactNode;
  if (isLoading) {
    content = (
      <div className='flex justify-center py-8'>
        <LoadingIndicator size='md' />
      </div>
    );
  } else if (error) {
    content = (
      <div className='p-4 bg-bg-error-secondary border border-border-error rounded-lg'>
        <p className='text-sm text-error-primary'>
          Couldn't load the available tools. Try refreshing the page.
        </p>
      </div>
    );
  } else if (!tools || tools.length === 0) {
    content = (
      <p className='text-sm text-fg-tertiary py-4'>
        No tools are currently available for this workspace.
      </p>
    );
  } else {
    content = (
      <div className='space-y-3'>
        {tools.map(tool => (
          <McpToolCard key={tool.name} tool={tool} />
        ))}
      </div>
    );
  }

  return (
    <SettingsBlock
      title='Available tools'
      description="Tools a connected MCP client can use on your behalf. This list reflects your workspace's entitlements – a client only sees what it's allowed to call."
    >
      {content}
    </SettingsBlock>
  );
}

export default function McpSettingsPage() {
  useTitle('MCP Integration');
  const { currentOrg } = useOrg();
  const { tokens, loading, error, revokeToken } = useOAuthTokens({
    orgId: currentOrg?.id,
  });

  const [copiedUrl, setCopiedUrl] = useState(false);
  const [selectedClientTab, setSelectedClientTab] =
    useState<Key>(DEFAULT_CLIENT_TAB);
  const handleTabChange = useCallback((key: Key) => {
    setSelectedClientTab(key);
  }, []);

  // Build MCP server URL from API base configuration
  const mcpServerUrl = useMemo(() => {
    const orgIdPart = currentOrg?.id ?? '';
    const apiBase = getApiBaseUrl();
    return apiBase
      ? `${apiBase}/api/orgs/${orgIdPart}/mcp/`
      : `${window.location.origin}/api/orgs/${orgIdPart}/mcp/`;
  }, [currentOrg?.id]);

  // claude.ai custom-connector deep link, pre-filled with connector name +
  // the org MCP server URL. connectorUrl MUST be percent-encoded (it is a full
  // URL with slashes and a colon); the #settings fragment goes after the query.
  const addToClaudeUrl = useMemo(() => {
    const connectorUrl = encodeURIComponent(mcpServerUrl);
    return `https://claude.ai/new?modal=add-custom-connector&connectorName=Ahoy&connectorUrl=${connectorUrl}#settings/customize-connectors`;
  }, [mcpServerUrl]);

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(mcpServerUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL to clipboard:', err);
    }
  }, [mcpServerUrl]);

  const handleRevokeToken = useCallback(
    (tokenId: number) => {
      revokeToken(tokenId).catch(err => {
        // Error is already logged by the hook and set in error state
        console.error('Revoke token failed:', err);
      });
    },
    [revokeToken]
  );

  if (!currentOrg) {
    return (
      <div className='flex justify-center items-center py-12'>
        <LoadingIndicator size='lg' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {error && (
        <div className='p-4 bg-bg-error-secondary border border-border-error rounded-lg'>
          <p className='text-sm text-error-primary'>{error}</p>
        </div>
      )}

      <div className='divide-y divide-border-secondary'>
        {/* Server URL — the primary connection action */}
        <SettingsBlock
          title='Server URL'
          description="Point your MCP client at this URL to connect it to your organization's CRM data."
        >
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
            {/* min-h matches the adjacent Button's natural sm-size height (40px)
                so the field and the CTA line up exactly. The button itself is
                `h-max`, so it can't be stretched — the field matches it. */}
            <div className='flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded-lg border border-border-secondary bg-bg-secondary pr-1 sm:min-h-10'>
              <code
                title={mcpServerUrl}
                className='min-w-0 flex-1 select-all truncate px-3 font-mono text-sm text-fg-secondary'
              >
                {mcpServerUrl}
              </code>
              <Button
                onClick={handleCopyUrl}
                color='tertiary'
                size='sm'
                iconLeading={copiedUrl ? Check : Copy01}
                aria-label={copiedUrl ? 'Copied' : 'Copy'}
                className='shrink-0'
              />
            </div>
            <Button
              href={addToClaudeUrl}
              target='_blank'
              rel='noopener noreferrer'
              color='primary'
              size='sm'
              iconTrailing={LinkExternal01}
              iconTrailingClassName='size-4 text-current opacity-70'
              className='shrink-0'
            >
              Add to Claude
            </Button>
          </div>
        </SettingsBlock>

        {/* How to Connect — per-client setup instructions */}
        <SettingsBlock
          title='How to Connect'
          description='Add the server URL to your MCP client, then sign in to Ahoy to authorize it via OAuth.'
        >
          <Tabs
            selectedKey={selectedClientTab}
            onSelectionChange={handleTabChange}
          >
            <TabList aria-label='MCP client setup instructions'>
              <Tab id='claude-ai'>Claude.ai</Tab>
              <Tab id='claude-desktop'>Claude Desktop</Tab>
              <Tab id='codex'>OpenAI Codex</Tab>
            </TabList>

            {/* Claude.ai — paths verified June 2026: support.claude.com/en/articles/11175166 */}
            <TabPanel id='claude-ai'>
              <div className='space-y-3'>
                <InstructionCard title='Personal (Pro / Max)'>
                  <StepList>
                    <li>Customize → Connectors</li>
                    <li>Click "+" → Add custom connector</li>
                    <li>Paste the Server URL above → Add</li>
                    <li>Sign in to Ahoy in the browser to authorize</li>
                  </StepList>
                </InstructionCard>
                <InstructionCard title='Team / Enterprise'>
                  <p className='text-sm text-fg-secondary'>
                    An Owner adds it once: Organization settings → Connectors →
                    Add → hover "Custom" → Web → paste the Server URL → Add.
                    Members then open Customize → Connectors and click Connect.
                  </p>
                </InstructionCard>
              </div>
            </TabPanel>

            {/* Claude Desktop — paths verified June 2026 */}
            <TabPanel id='claude-desktop'>
              <InstructionCard>
                <StepList>
                  <li>Settings → Connectors → Add custom connector</li>
                  <li>Paste the Server URL above</li>
                  <li>Sign in to Ahoy in the browser to authorize</li>
                </StepList>
              </InstructionCard>
            </TabPanel>

            {/* OpenAI Codex — verified July 2026: learn.chatgpt.com/docs/extend/mcp?surface=cli
                Remote streamable-HTTP + OAuth servers are configured in config.toml, then
                authorized with `codex mcp login`. */}
            <TabPanel id='codex'>
              <InstructionCard>
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <p className='text-sm text-fg-secondary'>
                      Add the server to{' '}
                      <code className='font-mono text-fg-primary'>
                        ~/.codex/config.toml
                      </code>
                      :
                    </p>
                    <CodeBlock
                      code={`[mcp_servers.ahoy]\nurl = "${mcpServerUrl}"`}
                    />
                    <p className='text-sm text-fg-tertiary'>
                      On recent Codex versions, add it with a single command
                      instead:
                    </p>
                    <CodeBlock
                      code={`codex mcp add ahoy --url ${mcpServerUrl}`}
                    />
                  </div>
                  <div className='space-y-2'>
                    <p className='text-sm text-fg-secondary'>
                      Then authorize and verify:
                    </p>
                    <StepList>
                      <li>
                        Run{' '}
                        <code className='font-mono text-fg-primary'>
                          codex mcp login ahoy
                        </code>{' '}
                        and sign in to Ahoy in the browser to authorize
                      </li>
                      <li>
                        Verify with{' '}
                        <code className='font-mono text-fg-primary'>/mcp</code>{' '}
                        inside Codex (or{' '}
                        <code className='font-mono text-fg-primary'>
                          codex mcp list
                        </code>
                        )
                      </li>
                    </StepList>
                  </div>
                </div>
              </InstructionCard>
            </TabPanel>
          </Tabs>

          {/* Generic catch-all note — applies to ChatGPT (help.openai.com/en/articles/12584461), Cursor (cursor.com/docs/mcp), and other MCP clients */}
          <p className='text-sm text-fg-tertiary'>
            Using a different client (ChatGPT, Cursor, etc.)? Add the Server URL
            above as a custom connector – most MCP clients follow the same
            pattern. Some require turning on a developer/advanced mode first.
            You'll be asked to sign in to Ahoy to authorize.
          </p>
        </SettingsBlock>

        {/* Available Tools */}
        <McpToolsSection orgId={currentOrg.id} />

        {/* Active OAuth Tokens */}
        <SettingsBlock
          title='Active connections'
          description='OAuth tokens are created automatically when you authorize an MCP client. Revoke one to disconnect that client immediately.'
        >
          {(() => {
            let tokensListContent: ReactNode;
            if (loading) {
              tokensListContent = (
                <div className='flex justify-center py-8'>
                  <LoadingIndicator size='md' />
                </div>
              );
            } else if (!error && tokens.length === 0) {
              tokensListContent = (
                <div className='rounded-xl border border-dashed border-border-secondary bg-bg-secondary px-4 py-8 text-center'>
                  <p className='text-sm text-fg-tertiary'>
                    No active connections yet. Connect an MCP client using the
                    instructions above to create your first token.
                  </p>
                </div>
              );
            } else {
              tokensListContent = (
                <div className='space-y-3'>
                  {tokens.map(token => (
                    <div
                      key={token.id}
                      className='flex items-center justify-between gap-4 rounded-xl border border-border-secondary bg-bg-primary p-4'
                    >
                      <div className='min-w-0 flex-1'>
                        <div className='flex items-center gap-2'>
                          <p className='truncate text-sm font-semibold text-fg-primary'>
                            {token.application_name}
                          </p>
                          <Badge type='color' size='sm' color='success'>
                            Active
                          </Badge>
                        </div>
                        <p className='mt-1 text-sm text-fg-tertiary'>
                          Scope: {token.scope}
                        </p>
                        <p className='mt-0.5 text-xs text-fg-quaternary'>
                          Created {new Date(token.created).toLocaleDateString()}{' '}
                          · Expires{' '}
                          {new Date(token.expires).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        onClick={() => handleRevokeToken(token.id)}
                        color='secondary-destructive'
                        size='sm'
                        iconLeading={Trash01}
                        aria-label={`Revoke ${token.application_name} connection`}
                      >
                        Revoke
                      </Button>
                    </div>
                  ))}
                </div>
              );
            }
            return tokensListContent;
          })()}
        </SettingsBlock>
      </div>
    </div>
  );
}
